/**
 * OutcomeBreakdownTable — the shared presentation for any `CampaignOutcomeItem[]`
 * (action-breakdown / "One outcome vocabulary across fan-out actions"). Renders one
 * distinct, separately-labelled group marker per outcome actually present (omitting
 * an outcome with no entries), plus a table listing every nośnik-and-window entry —
 * the aggregate groups collapse by nośnik, the listing does not (action-breakdown /
 * "Counts are per nośnik, not per window").
 *
 * Currently consumed by the campaign-confirmation dialog's pre-commit breakdown (task
 * 4.2); extracted as its own module here (task 4.1) so it is provably reusable by any
 * future fan-out action adopting the shared vocabulary
 * (`action-breakdown-remaining-dialogs`), without coupling to a specific dialog's
 * chrome. Deliberately does not touch `SaleBreakdownBody`/`record-sale-columns.tsx` —
 * the record-sale dialog keeps its own, unrelated `SalePreviewEntry`-based breakdown
 * unchanged (proposal.md: "no change to how it behaves").
 */
import * as React from 'react';
import { DataTable } from '@ooh/ui/components/data-table';
import { Badge } from '@ooh/ui/components/badge';
import type { TFunction } from 'i18next';
import type { CampaignOutcomeItem } from '@ooh/contracts';
import type { OfferUnitItem } from '../units-tab/units-tab-columns';
import {
  groupOutcomeCounts,
  joinOutcomeRows,
  OUTCOME_BADGE_VARIANT,
  OUTCOME_LABEL_KEY,
  useOutcomeBreakdownColumns,
  type LooseT,
} from './outcome-breakdown-columns';

export interface OutcomeBreakdownTableProps {
  t: TFunction<'offers'>;
  items: CampaignOutcomeItem[];
  units: OfferUnitItem[];
}

export function OutcomeBreakdownTable({ t, items, units }: OutcomeBreakdownTableProps) {
  const rows = React.useMemo(() => joinOutcomeRows(items, units), [items, units]);
  const groups = React.useMemo(() => groupOutcomeCounts(items), [items]);
  const columns = useOutcomeBreakdownColumns(t);
  const looseT = t as LooseT;
  // A queued entry's outcome depends on a hold another party currently owns, so it can
  // still change before the action is committed (action-breakdown / "A breakdown is a
  // snapshot, never a promise" — "A contended outcome is declared as uncertain up
  // front").
  const hasContendedOutcome = groups.some((group) => group.outcome === 'queued');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="outcome-breakdown-groups">
        {groups.map(({ outcome, count }) => (
          <div
            key={outcome}
            data-testid={`outcome-breakdown-group-${outcome}`}
            className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm"
          >
            <Badge variant={OUTCOME_BADGE_VARIANT[outcome]}>
              {looseT(OUTCOME_LABEL_KEY[outcome])}
            </Badge>
            <span className="font-semibold tabular-nums">{count}</span>
          </div>
        ))}
      </div>

      {hasContendedOutcome ? (
        <p className="text-xs text-muted-foreground" data-testid="outcome-breakdown-contended-note">
          {t('workspace.header.outcomeBreakdown.contendedNote')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={rows}
        getRowKey={(row) =>
          `${row.mediaUnitId}-${row.dateFrom.toISOString()}-${row.dateTo.toISOString()}`
        }
        getRowTestId={(row) =>
          `outcome-breakdown-row-${row.mediaUnitId}-${row.dateFrom.toISOString()}`
        }
        className="rounded-xl"
      />
    </div>
  );
}
